import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check, Mail, Phone, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { EASE } from "./Reveal";
import { useSEO } from "../hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODES = ["+91", "+1", "+44", "+61", "+65", "+971", "+974", "+966"];
const REASONS = [
  "Starting a Restaurant", "Launching a Food Brand", "Expanding an Existing Brand",
  "Planning a New Kitchen", "Optimising an Existing Kitchen", "Menu / Operations",
  "Cloud Kitchen", "QSR / Café", "Other",
];
const PLANNING = [
  "New Restaurant", "New Food Brand", "New Kitchen", "Expansion",
  "Existing Kitchen Improvement", "Multiple Locations", "Other",
];
const TIMES = ["09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM"];
const TIMEZONES = ["IST (UTC+5:30)", "GST (UTC+4)", "SGT (UTC+8)", "GMT (UTC+0)", "EST (UTC-5)"];
const LIVE_CALENDAR = "https://calendar.app.google.com/GDqmFJxYNs4yuKCa9";
const STEP_LABELS = ["Requirement", "Your Business", "Planning", "Schedule", "Confirm"];
const REASON_MAP = { "Cloud Kitchen": "Cloud Kitchen", QSR: "QSR / Café", "Café": "QSR / Café", Restaurant: "Starting a Restaurant", "Food Brand": "Launching a Food Brand" };

const inputCls = "mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300 placeholder:text-ink/30";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">{label}</span>
    {children}
  </label>
);

const Opt = ({ selected, onClick, children, testId }) => (
  <button
    type="button" onClick={onClick} data-testid={testId}
    className={`border px-4 py-3.5 text-left font-mono text-[11px] tracking-[0.1em] uppercase transition-colors duration-300 ${
      selected ? "border-skope bg-skope/10 text-skope" : "border-ink/15 text-ink/60 hover:border-ink/40 hover:text-ink"
    }`}
  >
    {children}
  </button>
);

export const ScheduleFlow = ({ snapshot, onClose, pageMode = false }) => {
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    reason: "", reasonOther: "", name: "", email: "", phoneCode: "+91", phone: "",
    brand: "", city: "", website: "", planning: "", requirements: "",
    preferredDate: "", preferredTime: "", timezone: "IST (UTC+5:30)",
  });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const mapped = snapshot?.businessType && REASON_MAP[snapshot.businessType];
    if (mapped) setD((p) => ({ ...p, reason: mapped }));
  }, [snapshot]);

  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const today = new Date().toISOString().split("T")[0];

  const valid = [
    !!d.reason && (d.reason !== "Other" || d.reasonOther.trim().length > 1),
    d.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email) && /^[0-9]{10}$/.test(d.phone) && d.brand.trim().length >= 2,
    !!d.planning,
    true,
    true,
  ][step];

  const next = () => { setError(""); if (valid) setStep((s) => s + 1); else setError("Please complete the required fields before continuing."); };

  const submit = async () => {
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/schedule-call`, {
        name: d.name, email: d.email, phone: `${d.phoneCode} ${d.phone}`,
        brand: d.brand, city: d.city, website: d.website,
        reason: d.reason === "Other" ? `Other: ${d.reasonOther}` : d.reason,
        planning: d.planning, requirements: d.requirements,
        preferredDate: d.preferredDate, preferredTime: d.preferredTime, timezone: d.timezone,
        calculator: snapshot || null,
      });
      setDone(true);
      setStep(4);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please email contact@skopekitchens.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={pageMode ? "max-w-[760px] mx-auto px-6" : ""} data-testid="schedule-flow">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-skope uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-skope" /> Schedule a Call
          </div>
          <p className="mt-3 text-sm text-ink/55 max-w-md">
            Tell us what you're building. We'll make sure the conversation starts in the right place.
          </p>
        </div>
        {!pageMode && (
          <button onClick={onClose} aria-label="Close scheduling" className="p-2 text-ink/40 hover:text-ink transition-colors" data-testid="schedule-close">
            <X size={22} />
          </button>
        )}
      </div>

      <div className="mt-8 flex items-center gap-2" data-testid="schedule-progress">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`font-mono text-[10px] tracking-[0.15em] uppercase whitespace-nowrap ${
              i < step ? "text-ink" : i === step ? "text-skope" : "text-ink/30"
            }`}>
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{i === step ? label : `0${i + 1}`}</span>
            </span>
            {i < STEP_LABELS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-skope" : "bg-ink/15"}`} />}
          </div>
        ))}
      </div>

      {snapshot && !done && (
        <div className="mt-6 border border-skope/30 bg-skope/[0.05] px-5 py-4" data-testid="schedule-snapshot">
          <div className="font-mono text-[10px] tracking-[0.25em] text-skope uppercase">Your Kitchen Snapshot</div>
          <div className="mt-1.5 font-mono text-xs text-ink/70">
            {snapshot.businessType} · {snapshot.dailyOrders} orders/day · {snapshot.serviceModel} · {snapshot.complexityBand} complexity · {snapshot.manpower}
          </div>
        </div>
      )}

      <div className="mt-8 min-h-[320px]">
        <motion.div key={step} initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }}>
          {step === 0 && (
            <div data-testid="schedule-step-1">
              <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-5">01 — Why are you getting in touch?</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {REASONS.map((r) => (
                  <Opt key={r} selected={d.reason === r} onClick={() => setD({ ...d, reason: r })} testId={`reason-${r.toLowerCase().replace(/[\s/]+/g, "-")}`}>{r}</Opt>
                ))}
              </div>
              {d.reason === "Other" && (
                <input value={d.reasonOther} onChange={set("reasonOther")} placeholder="Tell us briefly…" className={`${inputCls} mt-4`} data-testid="reason-other" />
              )}
            </div>
          )}

          {step === 1 && (
            <div data-testid="schedule-step-2">
              <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-5">02 — Tell us about your business</p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                <Field label="Name *"><input value={d.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="schedule-name" /></Field>
                <Field label="Email *"><input type="email" value={d.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" data-testid="schedule-email" /></Field>
                <Field label="Phone *">
                  <div className="mt-2 flex gap-3">
                    <select value={d.phoneCode} onChange={set("phoneCode")} aria-label="Country code" data-testid="schedule-phone-code"
                      className="w-24 shrink-0 bg-white border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300">
                      {CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input value={d.phone} onChange={set("phone")} inputMode="numeric" maxLength={10} placeholder="10-digit number" data-testid="schedule-phone"
                      className="flex-1 min-w-0 bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300 placeholder:text-ink/30" />
                  </div>
                </Field>
                <Field label="Brand / Company Name *"><input value={d.brand} onChange={set("brand")} className={inputCls} placeholder="Brand or company" data-testid="schedule-brand" /></Field>
                <Field label="City"><input value={d.city} onChange={set("city")} className={inputCls} placeholder="Where are you building?" data-testid="schedule-city" /></Field>
                <Field label="Website / Instagram (optional)"><input value={d.website} onChange={set("website")} className={inputCls} placeholder="https://…" data-testid="schedule-website" /></Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="schedule-step-3">
              <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-5">03 — What are you planning?</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {PLANNING.map((p) => (
                  <Opt key={p} selected={d.planning === p} onClick={() => setD({ ...d, planning: p })} testId={`planning-${p.toLowerCase().replace(/[\s/]+/g, "-")}`}>{p}</Opt>
                ))}
              </div>
            </div>
          )}

          {step === 3 && !done && (
            <div data-testid="schedule-step-4">
              <p className="font-mono text-xs text-ink/40 uppercase tracking-[0.2em] mb-5">04 — Your requirements & preferred time</p>
              <Field label="Tell us a little about what you're building.">
                <textarea rows={3} value={d.requirements} onChange={set("requirements")}
                  placeholder="Tell us about your concept, cuisine, current stage and what you need help with."
                  className={`${inputCls} resize-none`} data-testid="schedule-requirements" />
              </Field>
              <div className="grid sm:grid-cols-3 gap-6 mt-6">
                <Field label="Preferred date"><input type="date" min={today} value={d.preferredDate} onChange={set("preferredDate")} className={`${inputCls} bg-white`} data-testid="schedule-date" /></Field>
                <Field label="Preferred time">
                  <select value={d.preferredTime} onChange={set("preferredTime")} className={`${inputCls} bg-white`} data-testid="schedule-time">
                    <option value="">Select a time</option>
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Time zone">
                  <select value={d.timezone} onChange={set("timezone")} className={`${inputCls} bg-white`} data-testid="schedule-timezone">
                    {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <p className="mt-4 font-mono text-[10px] tracking-[0.1em] text-ink/40 uppercase">
                Your preferred time will be confirmed by the Skope team.
              </p>
              <a href={LIVE_CALENDAR} target="_blank" rel="noopener noreferrer"
                className="mt-5 flex items-center gap-3 border border-ink/15 px-5 py-4 hover:border-skope transition-colors group"
                data-testid="schedule-live-calendar">
                <CalendarDays size={16} className="text-skope shrink-0" />
                <span className="font-mono text-[11px] tracking-[0.1em] uppercase text-ink/60 group-hover:text-skope transition-colors">
                  Prefer to pick a live slot? Choose instantly on our calendar
                </span>
                <ArrowRight size={14} className="text-ink/30 group-hover:text-skope group-hover:translate-x-1 transition-all ml-auto" />
              </a>
            </div>
          )}

          {step === 4 && done && (
            <div className="text-left py-6" data-testid="schedule-confirm">
              <span className="w-12 h-12 border border-skope flex items-center justify-center"><Check size={20} className="text-skope" /></span>
              <h3 className="mt-6 font-display font-bold text-3xl lg:text-4xl text-ink tracking-tight">YOU'RE ALL SET.</h3>
              <p className="mt-4 text-ink/60 leading-relaxed max-w-md">
                Thanks for reaching out to Skope Kitchens. We look forward to learning about what you're building.
              </p>
              <p className="mt-3 text-ink/60 leading-relaxed max-w-md">
                Your request has been received. The Skope team will get back to you to confirm the call.
                {d.preferredDate && <> Preferred slot: <span className="text-ink">{d.preferredDate} · {d.preferredTime || "flexible"} · {d.timezone}</span>.</>}
              </p>
              <div className="mt-8 space-y-3">
                <a href="mailto:contact@skopekitchens.com" className="flex items-center gap-3 text-ink/70 hover:text-skope transition-colors" data-testid="schedule-confirm-email">
                  <Mail size={15} className="text-skope" /> <span className="font-mono text-sm">contact@skopekitchens.com</span>
                </a>
                <a href="tel:+918618689724" className="flex items-center gap-3 text-ink/70 hover:text-skope transition-colors" data-testid="schedule-confirm-phone">
                  <Phone size={15} className="text-skope" /> <span className="font-mono text-sm">+91 8618689724</span>
                </a>
              </div>
              <button onClick={onClose || (() => (window.location.href = "/"))}
                className="mt-10 bg-ink text-white font-mono text-xs tracking-[0.2em] uppercase px-10 py-4 hover:bg-skope hover:text-ink transition-colors"
                data-testid="schedule-back-home">
                Back to Skope
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {!done && (
        <div className="mt-8 pt-6 border-t border-ink/10">
          {error && <p className="mb-4 text-sm text-red-500" data-testid="schedule-error" role="alert">{error}</p>}
          <div className="flex items-center justify-between">
            <button onClick={() => { setError(""); setStep((s) => Math.max(0, s - 1)); }} disabled={step === 0}
              className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-ink/40 hover:text-ink transition-colors disabled:opacity-0"
              data-testid="schedule-back">
              <ArrowLeft size={14} /> Back
            </button>
            {step < 3 ? (
              <button onClick={next} disabled={!valid}
                className="flex items-center gap-3 bg-skope text-ink font-mono text-[11px] tracking-[0.2em] uppercase px-7 py-3.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="schedule-continue">
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={sending}
                className="flex items-center gap-3 bg-skope text-ink font-mono text-[11px] tracking-[0.2em] uppercase px-7 py-3.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
                data-testid="schedule-submit">
                {sending ? "Submitting…" : "Submit Request"} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ScheduleModal = () => {
  const [state, setState] = useState({ open: false, snapshot: null });
  useEffect(() => {
    const open = (e) => setState({ open: true, snapshot: e.detail || null });
    window.addEventListener("skope:open-schedule", open);
    return () => window.removeEventListener("skope:open-schedule", open);
  }, []);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setState((s) => ({ ...s, open: false }));
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  return (
    <AnimatePresence>
      {state.open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm"
          onClick={() => setState((s) => ({ ...s, open: false }))}
          data-testid="schedule-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="absolute inset-0 sm:inset-5 lg:inset-y-10 lg:left-1/2 lg:-translate-x-1/2 lg:w-[760px] lg:inset-x-auto bg-white overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-10 lg:p-12">
              <ScheduleFlow snapshot={state.snapshot} onClose={() => setState((s) => ({ ...s, open: false }))} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const SchedulePage = () => {
  useSEO({
    title: "Schedule a Call | Skope Kitchens",
    description: "Schedule a call with Skope Kitchens — tell us what you're building and we'll make sure the conversation starts in the right place.",
    canonical: "https://www.skopekitchens.com/schedule-a-call",
  });
  return (
    <div className="bg-white min-h-screen text-ink font-body" data-testid="schedule-page">
      <Navbar />
      <main className="pt-32 lg:pt-40 pb-24">
        <h1 className="sr-only">Schedule a Call with Skope Kitchens</h1>
        <ScheduleFlow pageMode onClose={null} />
      </main>
      <Footer />
    </div>
  );
};

export default SchedulePage;
